import { useCallback, useEffect, useState } from 'react';

import { supabase } from '@/components/atomic-crm/providers/supabase/supabase';

/**
 * Avisos de uma pessoa só (migration 240, `avisos_usuario`): celular de
 * contato repetido, transferência autorizada, limpeza de duplicados. É a
 * primeira fonte do sino que é POR USUÁRIO — o Feed é para todos e o aviso de
 * assembleia é calculado no aparelho. A RLS só devolve os da própria pessoa, e
 * ela só pode mexer no `lido_em`.
 */

export interface AvisoUsuario {
  id: number;
  criado_em: string;
  titulo: string;
  texto: string;
  link: string | null;
}

/** De quanto em quanto tempo o sino confere se chegou aviso novo. */
const INTERVALO_MS = 2 * 60 * 1000;

// Tabela da 240 ainda fora de database.generated.ts: cliente sem tipo, como em feedApi.ts.
const sb = supabase as unknown as { from: (table: string) => any };

export function useAvisosUsuario({ ativo = true }: { ativo?: boolean } = {}) {
  const [avisos, setAvisos] = useState<AvisoUsuario[]>([]);

  const recarregar = useCallback(async () => {
    if (!ativo) return setAvisos([]);
    try {
      const { data, error } = await sb
        .from('avisos_usuario')
        .select('id, criado_em, titulo, texto, link')
        .is('lido_em', null)
        .order('criado_em', { ascending: false })
        .limit(30);
      if (error) throw error;
      setAvisos(((data ?? []) as AvisoUsuario[]).filter((a) => a && a.id != null && a.titulo));
    } catch {
      // Tabela ainda não aplicada (ou fora do ar): o sino só fica quieto.
      setAvisos([]);
    }
  }, [ativo]);

  useEffect(() => {
    void recarregar();
    const t = window.setInterval(recarregar, INTERVALO_MS);
    window.addEventListener('focus', recarregar);
    return () => {
      window.clearInterval(t);
      window.removeEventListener('focus', recarregar);
    };
  }, [recarregar]);

  const marcarLidos = useCallback(async (ids: number[]) => {
    if (!ids.length) return;
    setAvisos((atuais) => atuais.filter((a) => !ids.includes(a.id)));
    try {
      await sb.from('avisos_usuario').update({ lido_em: new Date().toISOString() }).in('id', ids);
    } catch {
      // Falhou em gravar: volta a aparecer na próxima conferida, sem perder nada.
    }
  }, []);

  return { avisos, marcarLidos, recarregar };
}
